import { Component, Input, forwardRef, ElementRef, ViewChild, Output, EventEmitter, HostListener, ContentChild, TemplateRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { of } from "rxjs";
import { delay } from "rxjs/operators";

@Component({
  selector: 'inline-edit',
  host: {
    '[class.inline-edit]': 'someAttr',
  },
  template: `
    <div style="margin-top: .2rem; margin-bottom: .2rem" *ngIf="!editMode">
      <span *ngIf="type == 'input'">
        <span [ngClass]="{'overflow-ellipse': !wrap}" *ngIf="value && isCurrency">{{ value |currency }}</span>
        <span [ngClass]="{'overflow-ellipse': !wrap}" *ngIf="value && !isCurrency">{{ value }}</span>
        <span *ngIf="!value" class="text-muted">{{ noValueText }}</span>
      </span>
      <span *ngIf="type == 'select' && itemTemplate">
        <ng-template *ngIf="itemTemplate && value" [ngTemplateOutlet]="itemTemplate" [ngTemplateOutletContext]="{ $implicit: getDisplayValue() }"> </ng-template>
      </span>
      <span *ngIf="type == 'select' && !itemTemplate && value">{{ getDisplayValue() }}</span>
      <span *ngIf="type == 'select' && !value" class="text-muted">{{ noValueText }}</span>
    </div>

    <div *ngIf="editMode" class="input-group">
      <input (mousedown)="$event.stopPropagation()" #inputElement *ngIf="type == 'input'" [style.color]="fontColor" [style.font-size]="fontSize" [style.height]="inputHeight" [placeholder]="placeholder" type="text" style="margin-left: -.35rem; font-size: small" class="form-control" (keydown.enter)="$event.preventDefault()" (keyup.enter)="save($event)" [(ngModel)]="value"/>
      <select *ngIf="type == 'select'" class="form-control sm " [(ngModel)]="value">
        <ng-container *ngIf="!valuePrimitive">
          <option *ngFor="let item of items" [ngValue]="item[valueField]">{{ item[textField] }}</option>
        </ng-container>
        <ng-container *ngIf="valuePrimitive">
          <option *ngFor="let item of items" [ngValue]="item">
            <ng-template *ngIf="itemTemplate" [ngTemplateOutlet]="itemTemplate" [ngTemplateOutletContext]="{ $implicit: item }"></ng-template>
            <ng-container *ngIf="!itemTemplate">{{ item }}</ng-container>
          </option>
        </ng-container>
      </select>
      <div *ngIf="showButtons" class="input-group-append" id="button-addon4">
        <button class="btn btn-xs btn-success" type="button" (click)="save($event)">
          <i class="fas fa-check"></i>
        </button>
        <button class="btn btn-xs btn-danger" type="button" (click)="discard()">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
    <!-- <input #input  type="text" class="form-control"  (blur)="toggleEdit()" (change)="onChange()" /> -->
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InlineEditControl),
      multi: true,
    },
  ],
})
export class InlineEditControl implements ControlValueAccessor {
  @Input() value: string;
  @Input() type: 'input' | 'select' = 'input';
  @Input() items: any[];
  @Input() textField;
  @Input() valueField;
  @Input() valuePrimitive: boolean = false;
  @Input() wrap: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() showButtons: boolean = true;
  @Input() placeholder: string ='';
  @Input() fontSize: string;
  @Input() fontColor: string;
  @Input() inputHeight: string;
  @Input() isCurrency: boolean = false;
  @Input() noValueText: string = '--No Value--';
  @ContentChild(TemplateRef) itemTemplate: TemplateRef<ElementRef>;

  cssClass: string = '';
  editMode = false;
  @ViewChild('inputElement') inputElement: ElementRef;

  @Output()
  updated = new EventEmitter();

  @Output()
  isEditing = new EventEmitter<boolean>();

  someAttr: string = 'anyValue';
  originalValue: string;

  constructor(private elementRef: ElementRef) {
    this.cssClass = elementRef.nativeElement.getAttribute('class');
    elementRef.nativeElement.setAttribute('class', '');
  }

  @HostListener('document:click', ['$event'])
  onClick(event) {
    var externalSelectorClicked = (event.target as HTMLElement).classList.contains('p-colorpicker-preview');

    if (this.readOnly) {
      return;
    }

    if (externalSelectorClicked){
      return;
    }

    if (!this.editMode) {
      this.toggleEdit();
    }

    if ((!this.elementRef.nativeElement.contains(event.target) && !externalSelectorClicked) && this.editMode) {
      this.discard();
    }
  }

  toggleEdit() {
    this.editMode = !this.editMode;
    if (this.editMode) {
      of(null).pipe(delay(100)).subscribe(() => {
        if (this.inputElement?.nativeElement) {
          this.inputElement.nativeElement?.focus();
        }
      });
    }

    this.isEditing.emit(this.editMode);
  }

  save(evt) {


    var changed = this.originalValue != this.value;
    this.propagateChange(this.value);
    this.originalValue = this.value;
    this.propagateTouch();
    this.toggleEdit();

    if (changed) this.updated.emit(this.value);
    evt.preventDefault();
    evt.stopPropagation();
    return false;
  }

  getDisplayValue() {

    if (this.valuePrimitive) {
      return this.value;
    }

    var item = this.items.find((x) => x[this.valueField] == this.value);
    if (item) {
      return item[this.textField];
    }
  }

  discard() {
    this.value = this.originalValue;
    this.propagateChange(this.value);
    this.propagateTouch();
    this.toggleEdit();
  }

  propagateChange = (_: any) => {};
  propagateTouch = () => {};

  writeValue(value: string): void {
    this.value = value;
    this.originalValue = value;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  onChange() {
    this.propagateChange(this.value);
    this.propagateTouch();
  }

  onTouch() {
    this.propagateTouch();
  }
}
